import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Icon, Input, Loader, Message, Modal, Popup } from 'semantic-ui-react';

interface IProps {
  searchId: number;
  waitingFor?: string;
  allowMunicipalBulkApply?: boolean;
  searchLabel?: string;
  onSubmit: (queryString?: string) => Promise<void>;
}

interface WaitingForOption {
  id: number;
  reason: string;
  selected: boolean;
  customReason?: string;
}

export default function ContinWaiting(props: IProps) {
  const [waitingForOptions, setWaitingForOptions] = useState<WaitingForOption[]>([]);
  const [customReason, setCustomReason] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [applyToAllMunicipalSearches, setApplyToAllMunicipalSearches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  const [hasSavedReasons, setHasSavedReasons] = useState(Boolean(props.waitingFor?.trim()));

  useEffect(() => {
    setHasSavedReasons(Boolean(props.waitingFor?.trim()));
  }, [props.searchId, props.waitingFor]);

  useEffect(() => {
    if (!showOptions) return;
    let active = true;
    setLoading(true);
    setError('');
    setApplyToAllMunicipalSearches(false);
    setWaitingForOptions([]);
    setCustomReason('');
    Promise.all([
      axios.get<WaitingForOption[]>(`/api/continWaiting/GetSearchContinWaitingReasons/${props.searchId}`),
      axios.get<WaitingForOption[]>('/api/continWaiting/GetContinWaitingReasons'),
    ])
      .then(([{ data: selected }, { data: reasons }]) => {
        if (!active) return;
        setWaitingForOptions(
          reasons.map(reason => ({
            ...reason,
            selected: selected.some(option => option.id === reason.id),
          }))
        );
        setCustomReason(selected.find(option => option.reason === 'Custom')?.customReason || '');
        setHasSavedReasons(selected.length > 0);
      })
      .catch(() => {
        if (active) setError('Unable to load waiting reasons. Close the editor and try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [props.searchId, showOptions]);

  const save = async (clearWaiting: boolean) => {
    if (savingRef.current || loading) return;
    const selected = waitingForOptions.filter(option => option.selected);
    const hasCustom = selected.some(option => option.reason === 'Custom');
    if (!clearWaiting && selected.length === 0) {
      setError('Select at least one waiting reason, or use Clear waiting.');
      return;
    }
    if (!clearWaiting && hasCustom && !customReason.trim()) {
      setError('Enter a custom reason when Custom is selected.');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await axios.post(`/api/continWaiting/AddContinWaitingFor/${props.searchId}`, {
        reasons: clearWaiting ? [] : selected.map(option => option.id),
        customReason: !clearWaiting && hasCustom ? customReason.trim() : null,
        applyToAllMunicipalSearches: Boolean(props.allowMunicipalBulkApply && applyToAllMunicipalSearches),
        clearWaiting,
      });
    } catch (failure) {
      const message = failure.response?.data;
      setError(
        typeof message === 'string' ? message : 'Unable to save waiting reasons. Please try again.'
      );
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    setShowOptions(false);
    await props.onSubmit().catch(failure => console.error('Unable to refresh waiting reasons.', failure));
  };

  const close = () => {
    if (!savingRef.current) setShowOptions(false);
  };
  const label = props.searchLabel || 'search';
  const busy = loading || saving;

  return (
    <React.Fragment>
      <Popup
        content={props.waitingFor}
        disabled={showOptions || !props.waitingFor?.trim()}
        on={['hover', 'focus']}
        hoverable
        position="top left"
        style={{ bottom: 'auto', maxWidth: 400, overflowWrap: 'anywhere' }}
        trigger={
          <button
            type="button"
            aria-label={`Edit waiting reasons for ${label}`}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
            onClick={() => setShowOptions(true)}
          >
            <Icon name="hourglass half" circular color={hasSavedReasons ? 'red' : undefined} />
          </button>
        }
      />
      {showOptions && (
        <Modal style={{ padding: 25 }} open onClose={close}>
          <h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Please check off what you are waiting on
          </h3>
          {loading && <Loader active inline="centered" />}
          {error && <Message negative role="alert" content={error} />}
          <div style={{ display: 'flex', gap: 10, flexDirection: 'column', padding: 10 }}>
            {waitingForOptions.map(option => (
              <Checkbox
                key={option.id}
                label={option.reason}
                checked={option.selected}
                disabled={busy}
                onChange={() =>
                  setWaitingForOptions(options =>
                    options.map(item =>
                      item.id === option.id ? { ...item, selected: !item.selected } : item
                    )
                  )
                }
              />
            ))}
            <Input
              value={customReason}
              onChange={e => setCustomReason(e.target.value)}
              placeholder="Custom reason"
              label="Custom"
              disabled={
                busy || !waitingForOptions.some(option => option.reason === 'Custom' && option.selected)
              }
            />
            {props.allowMunicipalBulkApply && (
              <div style={{ marginTop: 12, paddingTop: 16, borderTop: '1px solid #ddd' }}>
                <Checkbox
                  label="Apply to all open municipal searches on this property"
                  checked={applyToAllMunicipalSearches}
                  disabled={busy}
                  onChange={(_, data) => setApplyToAllMunicipalSearches(Boolean(data.checked))}
                />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: 10 }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => save(true)}
              className="ui button"
              style={{ marginRight: 'auto' }}
            >
              Clear waiting
            </button>
            <button type="button" disabled={saving} onClick={close} className="ui button">
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !waitingForOptions.length}
              onClick={() => save(false)}
              className="ui primary button"
            >
              {saving ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
}
